import { gql, useLazyQuery } from '@apollo/client'

export interface ThreadsLocation {
  id: string
  name: string
  address?: string
  city?: string
  country?: string
  latitude?: number
  longitude?: number
  postalCode?: string
}

const SEARCH_THREADS_LOCATIONS_QUERY = gql`
  query SearchThreadsLocations($input: SearchThreadsLocationsInput!) {
    searchThreadsLocations(input: $input) {
      __typename
      ... on SearchThreadsLocationsResponse {
        locations {
          id
          name
          address
          city
          country
          latitude
          longitude
          postalCode
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`

// Define the GraphQL response types
interface SearchThreadsLocationsResponse {
  __typename: 'SearchThreadsLocationsResponse'
  locations: ThreadsLocation[]
}

interface MutationError {
  __typename: 'MutationError'
  message: string
}

type SearchThreadsLocationsPayload =
  | SearchThreadsLocationsResponse
  | MutationError

interface SearchThreadsLocationsQueryData {
  searchThreadsLocations?: SearchThreadsLocationsPayload
}

function isSearchThreadsLocationsResponse(
  response: SearchThreadsLocationsPayload | undefined,
): response is SearchThreadsLocationsResponse {
  return (
    !!response &&
    typeof response === 'object' &&
    '__typename' in response &&
    response.__typename === 'SearchThreadsLocationsResponse'
  )
}

const EMPTY_LOCATIONS_ARRAY: ThreadsLocation[] = []

export function useSearchThreadsLocations(): {
  searchLocations: (query: string, channelIds: string[]) => Promise<unknown>
  locations: ThreadsLocation[]
  loading: boolean
  errorMessage: string | null
} {
  const [searchThreadsLocations, { data, loading, error }] = useLazyQuery<
    SearchThreadsLocationsQueryData,
    { input: { query: string; channelIds: string[] } }
  >(SEARCH_THREADS_LOCATIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

  // Accepts array of channelIds
  const searchLocations = (query: string, channelIds: string[]) => {
    if (query.trim().length >= 2 && channelIds.length > 0) {
      return searchThreadsLocations({
        variables: {
          input: {
            query: query.trim(),
            channelIds,
          },
        },
      })
    }
    return Promise.resolve()
  }

  // Extract locations from response if successful, otherwise empty array
  let locations: ThreadsLocation[] = EMPTY_LOCATIONS_ARRAY
  let errorMessage: string | null = null
  const response = data?.searchThreadsLocations
  if (isSearchThreadsLocationsResponse(response)) {
    locations = response.locations || EMPTY_LOCATIONS_ARRAY
  } else if (response) {
    errorMessage = 'Failed to search Threads locations'
  } else if (error) {
    errorMessage = 'Failed to search Threads locations'
  }

  return {
    searchLocations,
    locations,
    loading,
    errorMessage,
  }
}
